import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, InputGroup,
  InputRightElement, useToast, SimpleGrid, Divider, Heading
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import SelectorPerfilUsuario from 'components/PerfilUsuario/SelectorPerfilUsuario';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const tiposOperacion = ['División', 'Subdivisión', 'Fusión', 'Segregación'];

const SolicitudDivisionTerrenos: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();

  const [tipoOperacion, setTipoOperacion] = useState(data?.tipoOperacion || '');
  const [claveCatastral, setClaveCatastral] = useState(data?.claveCatastral || '');
  const [superficie, setSuperficie] = useState(data?.superficie || '');
  const [fracciones, setFracciones] = useState(data?.fracciones || '');
  const [finalidad, setFinalidad] = useState(data?.finalidad || '');
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [costo, setCosto] = useState(data?.costo || 0);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>(null);

  const handlePerfil = (perfil: any) => {
    setPerfilSeleccionado(perfil);
  };

  useEffect(() => {
    const base = 150;
    setCosto(superficie * base);
  }, [superficie]);

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Revisa la dirección ingresada.',
          status: 'error',
        });
      }
    });
  };

  const handleGuardar = async () => {
    const payload = {
      tipoOperacion,
      claveCatastral,
      superficie,
      fracciones,
      finalidad,
      responsable,
      direccion,
      lat,
      lng,
      observaciones,
      costo,
      fechaRegistro: new Date(),
      usuarioId: perfilSeleccionado?.id || null,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/solicitud-division-terrenos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al guardar');

      toast({
        title: 'Guardado',
        description: 'La solicitud fue registrada.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al registrar.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={6} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
          <Heading size="md">Datos del Solicitante</Heading>
          <SelectorPerfilUsuario onPerfilSeleccionado={handlePerfil} />

          <Divider />

          <Heading size="md">Información de la Operación</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            <Select placeholder="Tipo de operación" value={tipoOperacion} onChange={(e) => setTipoOperacion(e.target.value)}>
              {tiposOperacion.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </Select>
            <Input placeholder="Clave catastral" value={claveCatastral} onChange={(e) => setClaveCatastral(e.target.value)} />
            <Input type="number" placeholder="Superficie (m²)" value={superficie} onChange={(e) => setSuperficie(parseFloat(e.target.value))} />
            <Input type="number" placeholder="No. fracciones" value={fracciones} onChange={(e) => setFracciones(parseInt(e.target.value))} />
            <Input placeholder="Finalidad" value={finalidad} onChange={(e) => setFinalidad(e.target.value)} />
            <Input placeholder="Responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
          </SimpleGrid>

          <Text fontWeight="semibold" color="green.600">
            Costo estimado: ${costo.toFixed(2)}
          </Text>

          <Divider />

          <Heading size="md">Ubicación del Terreno</Heading>
          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>

          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar Solicitud' : 'Actualizar Solicitud'}
          </Button>
        </>
      ) : (
        <>
          <Heading size="lg" mb={2}>Resumen de Solicitud</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
  <Text fontWeight="bold" mb={4} fontSize="lg">Información General</Text>
  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
    <Box><Text><strong>Tipo de operación:</strong> {tipoOperacion}</Text></Box>
    <Box><Text><strong>Clave catastral:</strong> {claveCatastral}</Text></Box>
    <Box><Text><strong>Superficie:</strong> {superficie} m²</Text></Box>
    <Box><Text><strong>Fracciones:</strong> {fracciones}</Text></Box>
    <Box><Text><strong>Finalidad:</strong> {finalidad}</Text></Box>
    <Box><Text><strong>Responsable:</strong> {responsable}</Text></Box>
    <Box><Text><strong>Dirección:</strong> {direccion}</Text></Box>
    <Box><Text><strong>Costo:</strong> ${costo.toFixed(2)}</Text></Box>
    <Box><Text><strong>Fecha:</strong> {data?.fechaRegistro ? new Date(data.fechaRegistro).toLocaleString() : 'No disponible'}</Text></Box>
  </SimpleGrid>
</Box>


            {observaciones && (
              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={2}>Observaciones</Text>
                <Text>{observaciones}</Text>
              </Box>
            )}
          </SimpleGrid>

          <Box h="250px" w="100%" borderRadius="md" overflow="hidden" border="1px solid #ccc">
            <GoogleMap zoom={15} center={{ lat, lng }} mapContainerStyle={{ width: '100%', height: '100%' }}>
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default SolicitudDivisionTerrenos;
